// ---------------------------------------------------------------------------
// bikeLanes — fetch + classify the bike infrastructure ALONG a route, so the
// map can draw it (Morgan: "what would be amazing is if we could show bike
// lanes"). Three classes, drawn in distinct colours by the map:
//   • track — a dedicated/protected cycleway, physically separated from cars.
//   • lane  — a painted on-road bike lane.
//   • path  — a shared path/footway where cycling is designated.
// ---------------------------------------------------------------------------

import { bbox, samplePolylineMax, nearestDistance, type LngLat } from "@/utils/polyline";
import { supabase } from "@/integrations/supabase/client";

export type BikeInfraType = "track" | "lane" | "path";

export interface BikeLaneSegment {
  type: BikeInfraType;
  coords: LngLat[];
}

type Tags = Record<string, string | undefined>;

/** Classify an OSM way's tags into a bike-infra type, or null if it isn't bike
 *  infrastructure we want to draw. Pure + unit-tested. Priority: a protected
 *  track outranks a painted lane outranks a shared path. */
export function classifyBikeInfra(tags: Tags = {}): BikeInfraType | null {
  const hw = (tags.highway ?? "").toLowerCase();
  const cw = (tags.cycleway ?? "").toLowerCase();
  const sideVals = [
    cw,
    (tags["cycleway:left"] ?? "").toLowerCase(),
    (tags["cycleway:right"] ?? "").toLowerCase(),
    (tags["cycleway:both"] ?? "").toLowerCase(),
  ];

  // Protected / separated track — a dedicated cycleway or a track-type lane.
  if (hw === "cycleway" || sideVals.some((v) => v === "track" || v === "separated")) {
    return "track";
  }
  // Painted on-road lane.
  if (sideVals.some((v) => v.includes("lane"))) {
    return "lane";
  }
  // Shared path / footway where bikes are designated.
  if (
    (hw === "path" || hw === "footway") &&
    (tags.bicycle === "designated" || tags.bicycle === "yes")
  ) {
    return "path";
  }
  // A cycleway value we didn't specifically match (e.g. "opposite") but that
  // still marks on-road provision → treat as a lane.
  if (cw && cw !== "no" && cw !== "separate") return "lane";
  return null;
}

interface OverpassWay {
  type?: string;
  tags?: Tags;
  geometry?: Array<{ lat: number; lon: number }>;
}

/**
 * Fetch the bike infrastructure within a small box around `routeCoords`, keep
 * only segments that run near the route, and classify each. Returns line
 * segments ready for the map. On-demand (toggle), so a single Overpass call.
 */
export async function fetchBikeLanesAlongRoute(
  routeCoords: LngLat[],
  opts: { nearM?: number } = {},
): Promise<BikeLaneSegment[]> {
  if (!Array.isArray(routeCoords) || routeCoords.length < 2) return [];
  const nearM = opts.nearM ?? 90;
  const b = bbox(routeCoords, 0.004); // pad ~400m around the route
  const tile = { south: b.minLat, west: b.minLng, north: b.maxLat, east: b.maxLng };

  let elements: OverpassWay[] = [];
  try {
    const { data, error } = await supabase.functions.invoke("overpass", {
      body: { tiles: [tile], kind: "bikelanes" },
    });
    if (error || !data) return [];
    elements = Array.isArray((data as { elements?: unknown }).elements)
      ? (data as { elements: OverpassWay[] }).elements
      : [];
  } catch {
    return [];
  }

  // Downsample the route once for the proximity test (keeps it cheap even on a
  // long ride with thousands of vertices).
  const routeRef = samplePolylineMax(routeCoords, 300);
  const out: BikeLaneSegment[] = [];
  for (const el of elements) {
    if (el.type !== "way" || !Array.isArray(el.geometry) || el.geometry.length < 2) continue;
    const type = classifyBikeInfra(el.tags ?? {});
    if (!type) continue;
    const coords: LngLat[] = el.geometry.map((g) => [g.lon, g.lat]);
    // Keep the segment if any vertex runs near the route.
    const near = coords.some((c) => nearestDistance(c, routeRef) <= nearM);
    if (near) out.push({ type, coords });
  }
  return out;
}
